import { useEffect, useRef, useState } from "react";
import { useUpdateCustomerDetails } from "@hooks/useUpdateCustomerDetails";
import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import { CustomerDetailsForm } from "@features/customerDetails/CustomerDetailsForm";
import { ICart } from "@features/cart/ICart";
import { useHistory } from "react-router-dom";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { Box } from "@mui/material";
import { FlyingCart } from "@features/cart/components/FlyingCart";
import { IFlyingCart } from "@features/cart/IFlyingCart";
import { IStepRoute } from "@common/StepperRouter";
import { IError, IFetchState } from "itsf-ui-common";
import { useStyle } from "./style";
import { useDeviceType } from "@hooks/useDeviceType";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { UserAlreadyExists } from "./UserAlreadyExists/UserAlreadyExists";

interface IParams {
    cart: ICart;
    flyingCart: IFlyingCart;
    nextStep?: IStepRoute;
    customerDetails?: ICustomerDetails;
    setCustomerDetails(customerDetails?: ICustomerDetails): void;
    getCustomerDetailsState: IFetchState;
    getCustomerDetails(): void;
    userAlreadyExistsEmail?: string;
    setUserAlreadyExistsEmail(email?: string): void;
}

export const CustomerDetailsPage = (props: IParams) => {
    const {
        cart,
        flyingCart,
        nextStep,
        customerDetails,
        setCustomerDetails,
        getCustomerDetailsState,
        getCustomerDetails,
        userAlreadyExistsEmail,
        setUserAlreadyExistsEmail,
    } = props;
    const { location } = useHistory();
    const formSubmitRef = useRef<(() => void) | null>(null);
    const { t } = useTranslation("cart");

    const [scrollToError, setScrollToError] = useState(false);
    const {
        updateCustomerDetails,
        updateCustomerDetailsState,
        updateCustomerPhoneNumberState,
        updateCustomerDeliveryAddressState,
        updateCustomerDetailsError,
    } = useUpdateCustomerDetails(nextStep);

    const { classes } = useStyle();
    const { isMobile } = useDeviceType();

    useEffect(() => {
        if (scrollToError) {
            const errorElement = document.querySelector('[id^="error-text-"]');

            if (errorElement) {
                const elementTop = errorElement.getBoundingClientRect().top + window.scrollY;
                const topWithOffset = Math.max(elementTop - 200, 0);
                window.scrollTo({ top: topWithOffset, behavior: "smooth" });
            }

            setScrollToError(false);
        }
    }, [scrollToError]);

    let retry;

    if (getCustomerDetailsState?.error && getCustomerDetails) {
        retry = getCustomerDetails;
    }

    if (updateCustomerDetailsError) {
        retry = () => window.location.reload();
    }

    const isLoading =
        (getCustomerDetailsState?.isLoading ?? false) ||
        updateCustomerDetailsState.isLoading ||
        updateCustomerPhoneNumberState.isLoading ||
        updateCustomerDeliveryAddressState.isLoading;

    const error: IError | undefined =
        (getCustomerDetailsState?.error as IError | undefined) ??
        updateCustomerPhoneNumberState.error ??
        updateCustomerDeliveryAddressState.error ??
        updateCustomerDetailsError;

    const updateDetails = (
        customerDetailsRequest: ICustomerDetails,
        privacyPolicy: boolean,
        recaptchaValue?: string
    ) => {
        const handleEmailError = (errorMessage?: string) => {
            setUserAlreadyExistsEmail(errorMessage ? customerDetailsRequest.email : undefined);
        };

        return updateCustomerDetails(
            customerDetailsRequest,
            cart,
            privacyPolicy,
            recaptchaValue,
            handleEmailError
        ).then(() => {
            setCustomerDetails(customerDetailsRequest);
        });
    };

    const handleNextClick = () => {
        if (formSubmitRef.current) {
            formSubmitRef.current();
        }
    };

    return (
        <div className={classes.containerBoxed}>
            <Helmet>
                <meta content={t("pageTitle.customerDetails")} property="og:title" />
                <meta content="noindex, nofollow" name="robots" />
                <title>{t("pageTitle.customerDetails")}</title>
            </Helmet>
            <HandleErrorAndLoading error={error} isLoading={isLoading} retryFetch={retry}>
                <Box display="flex" gap={4}>
                    {userAlreadyExistsEmail && (
                        <UserAlreadyExists
                            email={userAlreadyExistsEmail}
                            onUseDifferentEmail={() => {
                                setUserAlreadyExistsEmail(undefined);
                            }}
                        />
                    )}
                    {!userAlreadyExistsEmail && customerDetails && (
                        <Box maxWidth={isMobile ? "100%" : 700} width="100%">
                            <CustomerDetailsForm
                                cart={cart}
                                customerDetails={customerDetails}
                                pathName={location.pathname}
                                setScrollToError={setScrollToError}
                                updateCustomerDetails={updateDetails}
                                onSubmitForm={formSubmitRef}
                            />
                        </Box>
                    )}
                    {flyingCart && !userAlreadyExistsEmail && (
                        <FlyingCart
                            buttonType="submit"
                            flyingCart={flyingCart}
                            nextStep={nextStep}
                            onNextClick={handleNextClick}
                        />
                    )}
                </Box>
            </HandleErrorAndLoading>
        </div>
    );
};
